<script lang="ts" module>
  import { t } from './i18n.svelte'
  export interface ChatLine {
    from: string
    name: string
    text: string
    ts_ms: number
  }
</script>

<script lang="ts">
  import Icon from './Icon.svelte'
  import { decode, REACTIONS, tone, type Reaction, type Social } from './social'

  /**
   * The room's chat, used by both the host panel and the viewer page.
   *
   * Messages go through the signaling server's relay, which echoes every line to
   * everyone including the sender, so this list only ever renders what the
   * server sent: every participant sees the same order. Reactions and raised
   * hands travel the same way and show up here as small notes between messages.
   */
  let {
    messages,
    selfId: _selfId = null,
    onSend,
    onReact,
    disabled = false,
    bare = false,
  }: {
    messages: ChatLine[]
    selfId?: string | null
    onSend: (text: string) => void | Promise<void>
    /** When set, the input offers a reaction tray. */
    onReact?: (reaction: Reaction) => void
    disabled?: boolean
    /** No panel frame or title: the caller supplies the surrounding card. */
    bare?: boolean
  } = $props()

  const MAX_CHARS = 500
  let draft = $state('')
  let list = $state<HTMLOListElement | null>(null)
  let tray = $state(false)

  const reactionLabel: Record<Reaction, string> = {
    heart: t('sent a heart'),
    cheer: t('cheered'),
    laugh: t('is laughing'),
    fire: t('is on fire'),
    sad: t('is sad'),
  }

  // Keep the newest line in view as messages arrive.
  $effect(() => {
    void messages.length
    if (list) list.scrollTop = list.scrollHeight
  })

  async function submit(event: SubmitEvent) {
    event.preventDefault()
    const text = draft.trim()
    if (!text) return
    draft = ''
    await onSend(text)
  }

  function time(ts: number): string {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  /**
   * Twitch-style: every line is "name  message", reactions and hands included.
   * A run of the same reaction from one person folds into one row with a count,
   * so tapping a heart ten times is one line, not ten.
   */
  const rows = $derived.by(() => {
    const out: { key: number; line: ChatLine; item: Social; count: number }[] = []
    messages.forEach((line, key) => {
      const item = decode(line.text)
      const last = out[out.length - 1]
      if (
        item.kind === 'react' &&
        last?.item.kind === 'react' &&
        last.item.reaction === item.reaction &&
        last.line.from === line.from
      ) {
        last.count += 1
        last.line = line
        return
      }
      out.push({ key, line, item, count: 1 })
    })
    return out
  })
</script>

<div class={bare ? 'chat chat--bare' : 'panel stack chat'}>
  {#if !bare}<h2 class="panel__title">{t('Chat')}</h2>{/if}
  <ol class="chat__list" bind:this={list} aria-live="polite">
    {#each rows as row (row.key)}
      <li class="chat__row" title={time(row.line.ts_ms)}>
        <strong class="chat__name chat__name--{tone(row.line.name)}">{row.line.name}</strong>
        {#if row.item.kind === 'text'}
          <span class="chat__text">{row.item.text}</span>
        {:else if row.item.kind === 'react'}
          <span class="chat__event note-pop chat__note-icon--{row.item.reaction}">
            <Icon name={row.item.reaction} size={22} />
            {reactionLabel[row.item.reaction]}{row.count > 1 ? ` ×${row.count}` : ''}
          </span>
        {:else}
          <span class="chat__event note-pop chat__note-icon--hand" class:note-wave={row.item.up}>
            <Icon name="hand" size={22} />
            {row.item.up ? t('raised a hand') : t('lowered their hand')}
          </span>
        {/if}
      </li>
    {:else}
      <li class="faint chat__empty">{t('No messages yet.')}</li>
    {/each}
  </ol>
  {#if tray && onReact}
    <div class="chat__tray">
      {#each REACTIONS as reaction (reaction)}
        <button
          type="button"
          class="chat__tray-button chat__note-icon--{reaction}"
          aria-label={reactionLabel[reaction]}
          onclick={() => {
            onReact(reaction)
            tray = false
          }}><Icon name={reaction} size={20} /></button
        >
      {/each}
    </div>
  {/if}
  <form class="chat__form" onsubmit={submit}>
    <input
      bind:value={draft}
      placeholder={t('Say something')}
      maxlength={MAX_CHARS}
      {disabled}
      aria-label={t('Chat message')}
    />
    <span class="chat__actions">
      {#if onReact}
        <button
          type="button"
          class="chat__react"
          aria-label={t('Reactions')}
          aria-expanded={tray}
          {disabled}
          onclick={() => (tray = !tray)}><Icon name="smile" size={20} /></button
        >
      {/if}
      <button
        type="submit"
        class="primary chat__send"
        disabled={disabled || !draft.trim()}
        aria-label={t('Send')}><Icon name="send" size={18} /></button
      >
    </span>
  </form>
</div>
